import React from 'react';
import { Autocomplete, Box, Grid, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NS, PROFILE_KEYS } from '../../../i18n/constants/keys';
import { ComingSoonOverlay } from '../../../ui/mui';
import { SelectOptions } from '../../../utils/types/react_select_types';

interface IPersonalInfoFormProps {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  bio: string;
  isEditing: boolean;
  isEmployee: boolean;
  jobTitleOptions: SelectOptions[];
  selectedJobTitle: SelectOptions | null;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onBioChange: (value: string) => void;
  onJobTitleChange: (option: SelectOptions | null) => void;
}

export const PersonalInfoForm = ({
  firstName,
  lastName,
  email,
  phone,
  bio,
  isEditing,
  isEmployee,
  jobTitleOptions,
  selectedJobTitle,
  onFirstNameChange,
  onLastNameChange,
  onPhoneChange,
  onBioChange,
  onJobTitleChange,
}: IPersonalInfoFormProps) => {
  const { t } = useTranslation(NS.profile);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t(PROFILE_KEYS.fields.firstName)}
            value={firstName}
            fullWidth
            onChange={(e) => onFirstNameChange(e.target.value)}
            disabled={!isEditing}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t(PROFILE_KEYS.fields.lastName)}
            value={lastName}
            fullWidth
            onChange={(e) => onLastNameChange(e.target.value)}
            disabled={!isEditing}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label={t(PROFILE_KEYS.fields.email)} type="email" value={email} fullWidth disabled />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t(PROFILE_KEYS.fields.phone)}
            type="tel"
            value={phone}
            fullWidth
            onChange={(e) => onPhoneChange(e.target.value)}
            disabled={!isEditing}
          />
        </Grid>
        {isEmployee && (
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={jobTitleOptions}
              getOptionLabel={(option) => option.label}
              value={selectedJobTitle}
              onChange={(_e, option) => onJobTitleChange(option)}
              disabled={!isEditing}
              renderInput={(params) => <TextField {...params} label={t(PROFILE_KEYS.fields.jobTitle)} />}
              fullWidth
            />
          </Grid>
        )}
        <Grid item xs={12} sm={isEmployee ? 6 : 12}>
          <ComingSoonOverlay label={t(PROFILE_KEYS.actions.comingSoon)}>
            <TextField label={t(PROFILE_KEYS.fields.department)} value="" fullWidth disabled />
          </ComingSoonOverlay>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t(PROFILE_KEYS.fields.bio)}
            value={bio}
            multiline
            rows={4}
            fullWidth
            onChange={(e) => onBioChange(e.target.value)}
            disabled={!isEditing}
            placeholder={t(PROFILE_KEYS.fields.bioPlaceholder)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
